import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LinkAsButton, DescriptionList } from '@printdeal/ui-library';
import { getAttributeDescription, getLinkedServices, hasLinkedServices } from '@printdeal/business-logic';
import { Events, addDataLayerEntry } from '@printdeal/data-layer-manager';
import { Money } from '../../Money';

import { splitAttributeAndValues } from '../../../helpers/LineItems';
import LineItem from '../../../types/cart/LineItem';
import Order from '../../../types/order/Order';
import { OrderDetailCategories } from '../../../helpers/dataLayerHelpers/OrderDetailDataLayerHelper';

interface LineItemDescriptionProps {
  lineItem: LineItem;
  order?: Order;
  showItemCount?: number;
  productName?: string;
  toggleFullDescriptionCallback?: (isOpened: boolean) => void;
}

export interface IDescriptionItem {
  attributeName: string;
  attributeValue: string | number;
}

const LineItemDescription = ({
  lineItem,
  order,
  showItemCount = 3,
  productName = '',
  toggleFullDescriptionCallback = () => {},
}: LineItemDescriptionProps) => {
  const intl = useIntl();
  const intlAbbreviation = intl.locale.slice(0, 2);
  const productDescription = getAttributeDescription(lineItem, intlAbbreviation);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  const descriptionItems = splitAttributeAndValues(productDescription || '');

  const toggleFullDescription = () => {
    setIsDescriptionOpen(!isDescriptionOpen);
    toggleFullDescriptionCallback(!isDescriptionOpen);

    // GA4
    addDataLayerEntry('show_product_details');

    // GA3
    addDataLayerEntry(Events.Event, {
      gaEvent: {
        eventCategory: OrderDetailCategories.ORDER_DETAIL,
        eventAction: 'showProductdetails',
        eventNonInteraction: true,
      },
    });
  };

  return descriptionItems ? (
    <>
      <div className="pd-mb-3">
        {
          order && hasLinkedServices(lineItem.id, order) && (
            <>
              {
                getLinkedServices(lineItem.id, order).map((service) => (
                  <div key="file_design">
                    <div className="pd-inline">
                      <FormattedMessage
                        id="account.order.productDescription.service"
                        defaultMessage="Service"
                      />:
                    </div>
                    <div className="pd-inline">
                      { service.name[intlAbbreviation] ?? service.custom.fields.serviceName }
                      {' ('}
                      <Money
                        value={service.totalPrice.centAmount}
                        currency={service.totalPrice.currencyCode}
                      />
                      )
                    </div>
                  </div>
                ))
              }
            </>
          )
        }
        <DescriptionList>
          { productName && (
            <>
              <DescriptionList.Title>
                <FormattedMessage id="account.order.productDescription.productName" defaultMessage="Product name" />
              </DescriptionList.Title>
              <DescriptionList.Content>{productName}</DescriptionList.Content>
            </>
          )}
          {descriptionItems.slice(0, isDescriptionOpen ? Infinity : showItemCount)
            .map((descriptionItem: IDescriptionItem) => (
              <React.Fragment key={descriptionItem.attributeName}>
                <DescriptionList.Title data-cy-item-description-attributeName={descriptionItem.attributeName}>
                  {descriptionItem.attributeName}
                </DescriptionList.Title>
                <DescriptionList.Content data-cy-item-description-attributeValue={descriptionItem.attributeValue}>
                  {descriptionItem.attributeValue}
                </DescriptionList.Content>
              </React.Fragment>
            ))}
        </DescriptionList>
      </div>
      <LinkAsButton type="button" onClick={toggleFullDescription} variant="secondary" iconBefore={`double-chevron-${isDescriptionOpen ? 'up' : 'down'}`} data-cy="showMoreButton">
        {isDescriptionOpen ? (
          <FormattedMessage id="lineItems.list.hideFullDescription" defaultMessage="Show less" />
        ) : (
          <FormattedMessage id="lineItems.list.showFullDescription" defaultMessage="Show more" />
        )}
      </LinkAsButton>
    </>
  ) : (
    <p>{productDescription}</p>
  );
};

export default LineItemDescription;
